import { TmBaseComponent } from '@treasury/presentation';
import { checkIcon, depositIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-list';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ResearchedTransactionVm } from '../data/researched-transaction-view-model';
import './account-research-transactions-detail-sheet';

export const tagName = 'account-research-transactions-table';
@customElement(tagName)
export class AccountResearchTransactionsTable extends TmBaseComponent {
    @property({ type: Array })
    public transactions: ResearchedTransactionVm[] = [];

    @property({ type: Boolean })
    public isDesktop = false;

    @property({ type: Boolean })
    public loading = true;

    @state()
    private transactionDetailOpen = false;

    @state()
    private selectedTransaction?: ResearchedTransactionVm;

    private get columns() {
        if (this.isDesktop) {
            return [
                {
                    renderer: (t: ResearchedTransactionVm) =>
                        html`<div>${format(t.date, 'P')}</div>`,
                    options: { header: 'Date', textAlign: 'start' },
                },
                {
                    renderer: (t: ResearchedTransactionVm) =>
                        html`<div>${t.accountDisplayLabel}</div>`,
                    options: { header: 'Account', shrink: true, textAlign: 'start' },
                },
                {
                    renderer: (t: ResearchedTransactionVm) => {
                        return html`${t.checkImageNumber
                            ? html`<tm-icon class="pl-2 mt-[-10px]" .icon=${checkIcon}></tm-icon>`
                            : nothing}
                        ${t.isDeposit
                            ? html`<tm-icon class="pl-2 mt-[-10px]" .icon=${depositIcon}></tm-icon>`
                            : nothing}`;
                    },
                    options: { header: 'Check / Reference #', textAlign: 'start' },
                },
                {
                    renderer: (t: ResearchedTransactionVm) => {
                        return html`<div>${t.transactionCodeDescription}</div>`;
                    },
                    options: { header: 'Transaction Type', textAlign: 'start' },
                },
                {
                    renderer: (t: ResearchedTransactionVm) => {
                        return html`<div>${t.description}</div>`;
                    },
                    options: { header: 'Description', textAlign: 'start' },
                },
                {
                    renderer: (t: ResearchedTransactionVm) => {
                        return html`<div>${t.displayAmount}</div>`;
                    },
                    options: { header: 'Amount', textAlign: 'end', shrink: true },
                },
            ];
        }
        return [
            {
                renderer: (transaction: ResearchedTransactionVm) => html`
                    <div class="flex flex-col">
                        <span class="text-sm font-medium overflow-hidden text-ellipsis"
                            >${transaction.description}${transaction.checkNumber
                                ? html` | ${transaction.checkNumber}`
                                : nothing}</span
                        ><span class="text-xs text-secondary"
                            >${transaction.accountDisplayLabel} |
                            ${transaction.transactionCodeDescription}
                            ${transaction.checkImageNumber
                                ? html`<tm-icon
                                      class="pl-2 mt-[-10px]"
                                      .icon=${checkIcon}
                                  ></tm-icon>`
                                : nothing}
                            ${transaction.isDeposit
                                ? html`<tm-icon
                                      class="pl-2 mt-[-10px]"
                                      .icon=${depositIcon}
                                  ></tm-icon>`
                                : nothing}
                        </span>
                    </div>
                `,
                options: {
                    textAlign: 'start',
                    shrink: false,
                },
            },

            {
                renderer: (transaction: ResearchedTransactionVm) =>
                    html`<div class="flex flex-col">
                        <span class="text-sm font-bold">${transaction.displayAmount}</span>
                    </div>`,
                options: {
                    textAlign: 'end',
                    shrink: true,
                },
            },
        ];
    }

    protected render() {
        return html`
            <account-research-transactions-detail-sheet
                .open=${this.transactionDetailOpen}
                .transaction=${this.selectedTransaction}
                @close=${() => {
                    this.transactionDetailOpen = false;
                    this.selectedTransaction = undefined;
                }}
            ></account-research-transactions-detail-sheet>
            <tm-list
                .items=${this.transactions}
                .groupBy=${'displayDate'}
                .loading=${this.loading}
                .itemsDescriptor=${'transactions'}
                .disableStickyHeaders=${true}
                .searchFields=${[
                    'displayDate',
                    'accountName',
                    'accountNumber',
                    'accountType',
                    'description',
                    'displayAmount',
                    'transactionType',
                    'transactionId',
                    'checkNumber',
                    'displayCode',
                ]}
                .filterButton=${true}
                @select-list-item=${(e: CustomEvent) => {
                    this.selectedTransaction = e.detail;
                    this.transactionDetailOpen = true;
                }}
                @open-list-filter=${() => {
                    this.dispatchEvent(new CustomEvent('filter-transactions'));
                }}
                .columns=${this.columns}
            ></tm-list>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --grid-height: calc(100vh - 200px);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountResearchTransactionsTable;
    }
}
